import { RouteType } from 'types/RouteType';
import type RootStore from '../RootStore';

/**
 * Handles the first load of the router store by determining the initial route.
 * This function follows the Single Responsibility Principle by focusing only
 * on first load route determination.
 *
 * @param {RootStore} rootStore - The root store object containing the router store
 * @param {string | RouteType} defaultRoute - The default route to use if no route is found in the URL
 */
const loadRouteFromLocation = (
  rootStore: RootStore,
  defaultRoute: string | RouteType
) => {
  // Ensure router store exists before proceeding
  if (!rootStore.routerStore) {
    throw new Error('Router store is required but not found in root store');
  }

  // Attempt to load route information from the current URL
  const urlRouteInfo = rootStore.routerStore.loadRouteFromUrl(window.location.href);

  if (urlRouteInfo && urlRouteInfo.route) {
    // URL contains valid route information - use it for first load
    rootStore.routerStore.debugMode &&
      console.log('Loading route from URL for first load:', urlRouteInfo);

    rootStore.routerStore.setFirstLoad(
      urlRouteInfo.route,
      urlRouteInfo.params,
      urlRouteInfo.queryParams
    );
  } else if (defaultRoute) {
    // No valid route in URL - fall back to default route
    rootStore.routerStore.debugMode &&
      console.log('No valid route found in URL, using default route:', defaultRoute);

    rootStore.routerStore.setFirstLoad(defaultRoute);
  } else {
    // No URL route and no default route provided
    console.warn('No route information available for first load - router may not function correctly');
  }
};

export default loadRouteFromLocation;
